import * as React from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router';
import { bindActionCreators, Dispatch, AnyAction } from 'redux';
import { DropdownButtonType, List, ListItem, SingleSelectDropdown } from '@tableau/widgets';
import { Messages } from '../../../../../gen-src/Localize';
import { AppState } from '../../../../app/state/AppState';
import * as Layout from '../../../../common/styles/Layout';
import { changeContentType } from '../../../actions/ChangeContentTypeAction';
import { orderBy } from '../../../actions/OrderByAction';
import { ContentDefinitions } from '../../../models/contentDefinitions';
import { ContentTypeFilterValue, ContentTypeFilterAllOption } from '../../../models/ContentTypeFilterValue';
import { MixedContentTypeSearchResult } from '../../../models/SearchResultTypes';
import { NavigationService } from '../../../services/NavigationService';
import { DropdownBlueButton, DropdownDefaultButton, FilteringListClass } from '../../../styles/FilterStyles';
import { SortBy, SortSelection } from '../../../models/SortSelection';
import { ContentType } from '../../../models/ContentType';
import { FilterAction, TelemetryService } from '../../../services/TelemetryService';
import { formatTotalSearchResultCount } from '../../../../common/utils/L10n';
import { CommonStyles } from '../../../../common/styles/CommonStyles';
import { DefaultContentTypeDropdownOrder } from '../../../models/ContentTypeOrder';
import { ContentServiceError } from '../../../services/ContentService';
import { FeatureFlag, FeatureFlagService } from '../../../services/FeatureFlagService';

// eslint-disable-next-line prettier/prettier
export interface ContentTypeFilterOwnProps { }

export type ContentTypeFilterProps = ContentTypeFilterOwnProps &
  ContentTypeFilterStateProps &
  ContentTypeFilterDispatchProps &
  RouteComponentProps;

export function ContentTypeFilter(props: ContentTypeFilterProps): JSX.Element {
  const list: ListItem[] = [];
  const [prevValue, setPrevValue] = React.useState(props.selectedContentTypeFilterValue);

  if (props.searchResult) {
    list.push({
      content: (
        <div className={Layout.flexH}>
          <div className={Layout.flexAuto}>All</div>
          <div>{formatTotalSearchResultCount(props.totalCount)}</div>
        </div>
      ),
      value: ContentTypeFilterAllOption,
    });
    DefaultContentTypeDropdownOrder.forEach((contentType) => {
      if (contentType === 'Separator') {
        list.push({ separator: true });
      } else if (contentType in ContentType && props.searchResult?.map[contentType as ContentType]) {
        const resultCount = formatTotalSearchResultCount(props.searchResult.map[contentType as ContentType].totalCount);
        const ariaLabel = Messages.FilterDropdownOption_ariaLabel({
          COUNT_LOC: resultCount,
          COUNT: props.searchResult.map[contentType as ContentType].totalCount,
          ITEM: ContentDefinitions[contentType as ContentType].getSectionTitle(),
        });
        if (props.searchResult.map[contentType as ContentType].error !== ContentServiceError.SHOULD_NOT_BE_AVAILABLE) {
          list.push({
            content: (
              // The aria-label below will work with NVDA but not VoiceOver
              <div className={Layout.flexH} aria-label={ariaLabel}>
                <div className={Layout.flexAuto}>{ContentDefinitions[contentType as ContentType].getSectionTitle()}</div>
                <div>{resultCount}</div>
              </div>
            ),
            value: contentType,
          });
        }
      }
    });
  }
  const buttonStyle =
    props.selectedContentTypeFilterValue === ContentTypeFilterAllOption
      ? CommonStyles.Button.baseButtonStyle
      : CommonStyles.Button.blueButtonStyle;
  const buttonClass = props.selectedContentTypeFilterValue === ContentTypeFilterAllOption ? DropdownDefaultButton : DropdownBlueButton;

  return (
    <SingleSelectDropdown
      label=""
      list={<List items={list} className={FilteringListClass} />}
      type={DropdownButtonType.Outline}
      style={buttonStyle}
      value={props.selectedContentTypeFilterValue}
      onChange={(value) => onDropDownValueChanged(value)}
      className={buttonClass}
    >
      {props.selectedContentTypeFilterValue === ContentTypeFilterAllOption ? (
        <span>
          <span>{Messages.ContentTypeFilterLabel() + ' '}</span>
          <b>{Messages.ContentTypeFilterAllOption()}</b>
        </span>
      ) : (
        ContentDefinitions[props.selectedContentTypeFilterValue].getSectionTitle()
      )}
    </SingleSelectDropdown>
  );

  function onDropDownValueChanged(value: string) {
    const selectedContentTypeFilterValue = value as ContentTypeFilterValue;
    sendFiltersUpdatedTelemetryEvent(selectedContentTypeFilterValue);

    // If the new content type doesnt support the current sort order in the global state
    // then we must update the url to reflect the current available sort option
    let sortSelection = props.sortBy;
    if (value !== ContentTypeFilterAllOption) {
      const sortByList = ContentDefinitions[value as ContentType].getSortOptions();
      const selectionAvailableForContent = sortByList.find((x) => x.name === sortSelection?.name);
      if (!selectionAvailableForContent) {
        sortSelection = sortByList[0]; //use default sort option instead
      } else {
        sortSelection = selectionAvailableForContent; // make sure to use the content type specific sort selection for a particular name
      }
    } else {
      // if they navigate back to all content types use the relevancy default
      sortSelection = SortBy.getRelevancySortSelection();
      props.orderBy(sortSelection);
    }
    NavigationService.instance.navigateByContentType(selectedContentTypeFilterValue, sortSelection);

    setTimeout(() => {
      props.changeContentType(selectedContentTypeFilterValue);
    });
  }

  function sendFiltersUpdatedTelemetryEvent(value: ContentTypeFilterValue) {
    if (!FeatureFlagService.instance.getValue(FeatureFlag.searchProductMetrics)) {
      if (prevValue !== value && value === ContentTypeFilterAllOption) {
        TelemetryService.instance.filtersUpdatedEvent('contentType', FilterAction.Removed, value);
      } else if (prevValue === ContentTypeFilterAllOption && prevValue !== value) {
        TelemetryService.instance.filtersUpdatedEvent('contentType', FilterAction.Added, value);
      } else if (prevValue !== value) {
        TelemetryService.instance.filtersUpdatedEvent('contentType', FilterAction.Updated, value);
      }
    } else {
      if (prevValue !== value && value === ContentTypeFilterAllOption) {
        TelemetryService.instance.filtersUpdatedEvent('contentType', FilterAction.Removed, value, props.searchTerm);
      } else if (prevValue === ContentTypeFilterAllOption && prevValue !== value) {
        TelemetryService.instance.filtersUpdatedEvent('contentType', FilterAction.Added, value, props.searchTerm);
      } else if (prevValue !== value) {
        TelemetryService.instance.filtersUpdatedEvent('contentType', FilterAction.Updated, value, props.searchTerm);
      }
    }
    setPrevValue(value);
  }
}

export interface ContentTypeFilterStateProps {
  searchResult?: MixedContentTypeSearchResult;
  searchTerm: string;
  totalCount: number;
  selectedContentTypeFilterValue: ContentTypeFilterValue;
  sortBy?: SortSelection;
}

export interface ContentTypeFilterDispatchProps {
  changeContentType: (contentTypeFilterValue: ContentTypeFilterValue) => void;
  orderBy: (sortSelection: SortSelection) => void;
}

const mapStateToProps = (state: AppState) => ({
  searchResult: state.search.mixedContentType.searchResult,
  totalCount: state.search.totalCount,
  searchTerm: state.search.searchTerm,
  selectedContentTypeFilterValue: state.search.selectedContentTypeFilterValue,
  sortBy: state.search.sortBy,
});

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>) =>
  bindActionCreators(
    {
      changeContentType,
      orderBy,
    },
    dispatch
  );

const ContentTypeFilterWithRouter = withRouter(ContentTypeFilter);

export const ConnectedContentTypeFilter: React.ComponentClass<ContentTypeFilterOwnProps> = connect(
  mapStateToProps,
  mapDispatchToProps
)(ContentTypeFilterWithRouter);
